/**
 * invoices.ts — service layer for the Finance > Invoicing engine (Engine 1).
 *
 * Turns the InvoicesPage shell into real working logic. All persistence goes
 * through the live Supabase `invoices` table: the React app talks to Supabase
 * directly, and the FastAPI bridge (apiClient.ts) is off by default and has no
 * `/invoices` route, so this service is Supabase-only by design.
 *
 * Public API:
 *   - computeInvoiceTotal(lineItems)  pure SUM(qty * unit_price), 2-dp safe
 *   - createInvoice(input)            validates, derives the total, persists
 *   - listInvoices()                  live (non-deleted) invoices, newest first
 *   - getInvoice(id)                  one live invoice, or null
 *   - setInvoiceStatus(id, status)    validated status change
 *   - softDeleteInvoice(id)           is_deleted = true (never hard-delete)
 *
 * Status set (product decision 2026-06-04): draft | sent | paid | void |
 *   overdue | cancelled. `void` (invoice nullified) and `cancelled` (deal
 *   called off) are intentionally DISTINCT and never mapped onto each other.
 *
 * Deliberate scope decisions for this engine (tracked as follow-ups):
 *   - workspace_id IS stamped on insert (the signed-in person's workspace) as step 1
 *     of the stamp → backfill → RLS-policy hardening. Safe and behavior-neutral while
 *     invoices RLS is off; backfilling existing NULL rows and enabling the
 *     workspace-scoped policy are the remaining follow-ups.
 *   - Audit logging IS wired: each mutation (create / update / status-change /
 *     delete) fires writeAudit (src/lib/audit.ts) AFTER it succeeds, as a
 *     fire-and-forget side-effect. writeAudit is defensive (self-catching), so
 *     an audit failure can never throw or roll back the invoice operation.
 *
 * Requires migration 0004 (invoices.line_items jsonb) applied to Supabase.
 */

import { supabase } from './supabase';
import { getCurrentWorkspaceId, requireWorkspaceId } from './apiClient';
import { writeAudit } from './audit';

export type InvoiceStatus =
  | 'draft'
  | 'sent'
  | 'paid'
  | 'void'
  | 'overdue'
  | 'cancelled';

/** Every status the service will accept on a write. */
export const INVOICE_STATUSES: readonly InvoiceStatus[] = [
  'draft',
  'sent',
  'paid',
  'void',
  'overdue',
  'cancelled',
];

/** One billable line on an invoice. The total is derived from these. */
export interface InvoiceLineItem {
  description: string;
  quantity: number;
  unit_price: number;
}

/** A row of the `invoices` table (post-0004). */
export interface Invoice {
  id: string;
  workspace_id: string | null;
  invoice_number: string;
  client_name: string;
  client_email: string | null;
  issue_date: string; // ISO date (YYYY-MM-DD)
  due_date: string; // ISO date
  tax_rate: number; // percent, e.g. 18 (0 = tax-exempt/export)
  subtotal: number; // SUM(quantity * unit_price)
  tax_amount: number; // round(subtotal * tax_rate / 100)
  amount: number; // grand total = subtotal + tax_amount (kept as the canonical total for back-compat)
  status: InvoiceStatus;
  notes: string | null;
  deal_id: string | null;
  line_items: InvoiceLineItem[];
  is_deleted: boolean;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Fields a caller supplies to create an invoice. `amount` is NOT accepted — it is always derived. */
export interface CreateInvoiceInput {
  invoice_number: string;
  client_name: string;
  client_email?: string | null;
  issue_date: string;
  due_date: string;
  status?: InvoiceStatus;
  notes?: string | null;
  deal_id?: string | null;
  line_items: InvoiceLineItem[];
  /**
   * Tax rate as a percent (e.g. 18). 0 is valid (tax-exempt/export). When omitted
   * (null/undefined), the workspace default_tax_rate is used (falling back to 18).
   */
  tax_rate?: number | null;
}

/** The three derived money figures for an invoice. */
export interface InvoiceAmounts {
  subtotal: number;
  tax_amount: number;
  total: number;
}

const TABLE = 'invoices';

/** Round to 2 decimals without binary-float drift (e.g. 0.1 + 0.2). */
function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/**
 * Pure total calculator: SUM(quantity * unit_price), rounded to 2 dp.
 * Defensive — a malformed row (missing / non-numeric qty or price) counts
 * as 0 rather than turning the whole total into NaN.
 */
export function computeInvoiceTotal(
  lineItems: InvoiceLineItem[] | null | undefined,
): number {
  if (!Array.isArray(lineItems)) return 0;
  const total = lineItems.reduce((sum, li) => {
    const qty = Number(li?.quantity);
    const price = Number(li?.unit_price);
    if (!Number.isFinite(qty) || !Number.isFinite(price)) return sum;
    return sum + qty * price;
  }, 0);
  return round2(total);
}

/**
 * Compute all three money figures from line items + a tax rate (percent):
 *   subtotal   = SUM(quantity * unit_price)
 *   tax_amount = round(subtotal * taxRate/100, 2)
 *   total      = round(subtotal + tax_amount, 2)
 * Defensive: a non-numeric taxRate is treated as 0. Reuses the 2-dp rounding.
 */
export function computeInvoiceAmounts(
  lineItems: InvoiceLineItem[] | null | undefined,
  taxRate: number | null | undefined,
): InvoiceAmounts {
  const subtotal = computeInvoiceTotal(lineItems);
  const rate = Number.isFinite(Number(taxRate)) ? Number(taxRate) : 0;
  const tax_amount = round2((subtotal * rate) / 100);
  const total = round2(subtotal + tax_amount);
  return { subtotal, tax_amount, total };
}

/**
 * Read the workspace default tax rate, used when an invoice is created/updated
 * without an explicit tax_rate. Falls back to 18 if the column/row is absent or
 * the query fails — never throws (a tax-rate lookup must not block invoicing).
 */
async function getWorkspaceDefaultTaxRate(): Promise<number> {
  try {
    // No workspace resolved yet = no row to read. Take the same 18 the query failure
    // takes, rather than reading company one's rate.
    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) return 18;
    const { data, error } = await supabase
      .from('workspaces')
      .select('default_tax_rate')
      .eq('id', workspaceId)
      .maybeSingle();
    if (error || !data) return 18;
    const rate = Number((data as { default_tax_rate?: number | null }).default_tax_rate);
    return Number.isFinite(rate) ? rate : 18;
  } catch {
    return 18;
  }
}

/** Clean a line-item list before persisting; drops fully-empty rows. */
function normalizeLineItems(lineItems: InvoiceLineItem[]): InvoiceLineItem[] {
  if (!Array.isArray(lineItems)) return [];
  return lineItems
    .map((li) => ({
      description: String(li?.description ?? '').trim(),
      quantity: Number(li?.quantity) || 0,
      unit_price: Number(li?.unit_price) || 0,
    }))
    .filter(
      (li) => li.description !== '' || li.quantity !== 0 || li.unit_price !== 0,
    );
}

/**
 * Throw a tagged Error. Pass a PostgREST error as `from` to carry its diagnostic
 * fields through to the page — `details`/`hint` are usually the ones that name the
 * actual fix (e.g. hint: "Perhaps you meant 'campaign_id'" on a PGRST200). Plain
 * validation failures pass no `from` (or a bare code string) and are unaffected.
 */
function fail(
  message: string,
  from?: string | { code?: string; details?: string; hint?: string },
): never {
  const err = new Error(message) as Error & { code?: string; details?: string; hint?: string };
  if (typeof from === 'string') {
    err.code = from;
  } else if (from) {
    if (from.code) err.code = from.code;
    if (from.details) err.details = from.details;
    if (from.hint) err.hint = from.hint;
  }
  throw err;
}

/**
 * Create an invoice. The total (`amount`) is computed from the line items —
 * a caller-supplied amount is never trusted. Status defaults to 'draft'.
 * Returns the saved row. Throws (with the supabase error code attached) on
 * failure so the caller's try/catch can surface the cause.
 */
export async function createInvoice(input: CreateInvoiceInput): Promise<Invoice> {
  if (!input.invoice_number?.trim()) fail('Invoice number is required.');
  if (!input.client_name?.trim()) fail('Client name is required.');
  if (!input.issue_date) fail('Issue date is required.');
  if (!input.due_date) fail('Due date is required.');

  const status: InvoiceStatus = input.status ?? 'draft';
  if (!INVOICE_STATUSES.includes(status)) fail(`Invalid status: ${status}`);

  const line_items = normalizeLineItems(input.line_items ?? []);
  // tax_rate defaults to the workspace default (then 18) only when truly omitted;
  // 0 is a valid, explicit rate (tax-exempt/export) and must be preserved.
  const tax_rate = input.tax_rate == null ? await getWorkspaceDefaultTaxRate() : input.tax_rate;
  const { subtotal, tax_amount, total } = computeInvoiceAmounts(line_items, tax_rate);

  const row = {
    workspace_id: requireWorkspaceId(),
    invoice_number: input.invoice_number.trim(),
    client_name: input.client_name.trim(),
    client_email: input.client_email?.trim() || null,
    issue_date: input.issue_date,
    due_date: input.due_date,
    tax_rate,
    subtotal,
    tax_amount,
    amount: total, // grand total — canonical for everything that reads invoice.amount
    status,
    notes: input.notes?.trim() || null,
    deal_id: input.deal_id ?? null,
    line_items,
  };

  const { data, error } = await supabase
    .from(TABLE)
    .insert(row)
    .select('*')
    .single();

  if (error) fail(error.message, error);

  const created = data as Invoice;
  // Fire-and-forget audit (writeAudit self-catches; never blocks the return).
  void writeAudit({
    resource_type: 'invoices',
    action: 'created',
    resource_id: created.id,
    metadata: { invoice_number: row.invoice_number, amount: row.amount, tax_rate, status: row.status },
  });
  return created;
}

/**
 * Update an invoice. Mirrors createInvoice: same validation, and the total
 * (`amount`) is RECOMPUTED from line_items — a caller-supplied amount is never
 * trusted. Deliberately does NOT touch workspace_id, is_deleted, or deleted_at,
 * so the tenant + soft-delete state are preserved. Throws on failure.
 */
export async function updateInvoice(id: string, input: CreateInvoiceInput): Promise<Invoice> {
  if (!input.invoice_number?.trim()) fail('Invoice number is required.');
  if (!input.client_name?.trim()) fail('Client name is required.');
  if (!input.issue_date) fail('Issue date is required.');
  if (!input.due_date) fail('Due date is required.');

  const status: InvoiceStatus = input.status ?? 'draft';
  if (!INVOICE_STATUSES.includes(status)) fail(`Invalid status: ${status}`);

  const line_items = normalizeLineItems(input.line_items ?? []);
  const tax_rate = input.tax_rate == null ? await getWorkspaceDefaultTaxRate() : input.tax_rate;
  const { subtotal, tax_amount, total } = computeInvoiceAmounts(line_items, tax_rate);

  const { data, error } = await supabase
    .from(TABLE)
    .update({
      invoice_number: input.invoice_number.trim(),
      client_name: input.client_name.trim(),
      client_email: input.client_email?.trim() || null,
      issue_date: input.issue_date,
      due_date: input.due_date,
      tax_rate,
      subtotal,
      tax_amount,
      amount: total,
      status,
      notes: input.notes?.trim() || null,
      deal_id: input.deal_id ?? null,
      line_items,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select('*')
    .single();

  if (error) fail(error.message, error);

  const updated = data as Invoice;
  // Fire-and-forget audit (writeAudit self-catches; never blocks the return).
  void writeAudit({
    resource_type: 'invoices',
    action: 'updated',
    resource_id: id,
    metadata: { invoice_number: updated.invoice_number, amount: total, tax_rate, status },
  });
  return updated;
}

/** List live (non-deleted) invoices, newest first. */
export async function listInvoices(): Promise<Invoice[]> {
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('is_deleted', false)
    .order('created_at', { ascending: false });

  if (error) fail(error.message, error);
  return (data ?? []) as Invoice[];
}

/** Fetch a single live invoice by id, or null if not found. */
export async function getInvoice(id: string): Promise<Invoice | null> {
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('id', id)
    .eq('is_deleted', false)
    .maybeSingle();

  if (error) fail(error.message, error);
  return (data as Invoice) ?? null;
}

/** Change an invoice's status (validated against INVOICE_STATUSES). */
export async function setInvoiceStatus(
  id: string,
  status: InvoiceStatus,
): Promise<void> {
  if (!INVOICE_STATUSES.includes(status)) fail(`Invalid status: ${status}`);
  const { error } = await supabase
    .from(TABLE)
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) fail(error.message, error);

  // Fire-and-forget audit (writeAudit self-catches; never blocks the return).
  void writeAudit({
    resource_type: 'invoices',
    action: 'status_changed',
    resource_id: id,
    metadata: { status },
  });
}

/** Soft-delete an invoice (Golden Rule: never hard-delete). */
export async function softDeleteInvoice(id: string): Promise<void> {
  const now = new Date().toISOString();
  const { error } = await supabase
    .from(TABLE)
    .update({ is_deleted: true, deleted_at: now, updated_at: now })
    .eq('id', id);
  if (error) fail(error.message, error);

  // Fire-and-forget audit (writeAudit self-catches; never blocks the return).
  void writeAudit({
    resource_type: 'invoices',
    action: 'deleted',
    resource_id: id,
  });
}
